import React, { useState } from 'react';
import { Link } from 'react-router';
import { CheckCircle, DollarSign, TrendingUp, Shield, Users, ArrowRight, Clock, Languages, HeartPulse, Target, Zap } from 'lucide-react';

const translations = {
    en: {
        nav: {
            login: "Partner Login",
            signup: "Become a Partner"
        },
        hero: {
            badge: "🤝 DrChamber Affiliate Program",
            title: "Earn Money by Digitalizing Healthcare in Bangladesh",
            subtitle: "Join our partner program and earn 20% commission for every doctor you refer. Help doctors switch to smart prescriptions while building your income.",
            cta1: "Become a Partner",
            cta2: "Partner Login"
        },
        howItWorks: {
            title: "How It Works?",
            subtitle: "Start earning in 3 simple steps",
            steps: [
                {
                    number: "1",
                    title: "Register",
                    desc: "Create your partner account in 1 minute. You will get a unique dashboard and referral link."
                },
                {
                    number: "2",
                    title: "Refer Doctors",
                    desc: "Share your link with doctors or clinics. When they register using your link, they are tagged to you forever."
                },
                {
                    number: "3",
                    title: "Get Paid",
                    desc: "Earn 20% commission when they upgrade to a paid plan. Withdraw earnings via bKash/Nagad."
                }
            ]
        },
        benefits: {
            title: "Why Partner with Us?",
            subtitle: "We provide the best tools and transparency so you can focus on marketing while we handle the technology.",
            items: [
                {
                    icon: "dollar",
                    title: "High Commission Rate",
                    desc: "Flat 20% commission on every successful subscription payment. If a doctor pays ৳6,000, you get ৳1,200."
                },
                {
                    icon: "trending",
                    title: "Real-Time Tracking",
                    desc: "See clicks, registrations, and earnings instantly on your dedicated partner dashboard."
                },
                {
                    icon: "shield",
                    title: "Easy Withdrawals",
                    desc: "Request payout anytime once your balance reaches ৳500. We process payments via bKash, Nagad, or Bank."
                }
            ]
        },
        commission: {
            title: "Commission Example",
            monthly: "Doctor subscribes (Monthly)",
            monthlyPrice: "৳ 600",
            monthlyCommission: "Your Commission (20%)",
            monthlyEarnings: "+ ৳ 120",
            yearly: "Doctor subscribes (Yearly)",
            yearlyPrice: "৳ 6,000",
            yearlyCommission: "Your Commission (20%)",
            yearlyEarnings: "+ ৳ 1,200"
        },
        policies: {
            title: "Program Policies",
            subtitle: "Important rules to keep the system fair for everyone.",
            policies: [
                {
                    icon: "clock",
                    title: "7-Day Hold Period",
                    desc: "Commissions are marked as 'Pending' for 7 days after the payment to protect against refunds or fraud. After 7 days, it moves to your wallet automatically."
                },
                {
                    icon: "shield",
                    title: "No Self-Referral",
                    desc: "You cannot use your own referral link to create a doctor account for yourself. Self-referrals will be detected and commission will be voided."
                },
                {
                    icon: "check",
                    title: "Payment Validation",
                    desc: "Commission is generated ONLY when a doctor makes a successful payment via bKash. Free trial registrations do not generate commissions."
                },
                {
                    icon: "dollar",
                    title: "Withdrawal Limit",
                    desc: "Minimum withdrawal amount is ৳500. You can request a withdrawal anytime, and it will be processed within 24-48 hours."
                }
            ]
        },
        faq: {
            title: "Top Earners",
            subtitle: "See what our successful partners are earning",
            earnings: [
                { name: "Md. Karim", referrals: "50+ doctors", monthly: "৳ 15,000+", image: "👨‍💼" },
                { name: "Fatima Khan", referrals: "35+ doctors", monthly: "৳ 10,500+", image: "👩‍💼" },
                { name: "Ahmed Hassan", referrals: "28+ doctors", monthly: "৳ 8,400+", image: "👨‍💼" }
            ]
        },
        cta: {
            title: "Ready to Start Earning?",
            subtitle: "No credit card required. Free registration.",
            button: "Join Partner Program Now"
        }
    },
    bn: {
        nav: {
            login: "পার্টনার লগইন",
            signup: "পার্টনার হন"
        },
        hero: {
            badge: "🤝 DrChamber অ্যাফিলিয়েট প্রোগ্রাম",
            title: "বাংলাদেশে স্বাস্থ্যসেবা ডিজিটালাইজ করে আয় করুন",
            subtitle: "আমাদের পার্টনার প্রোগ্রামে যোগ দিন এবং প্রতিটি রেফার করা ডাক্তারের জন্য ২০% কমিশন অর্জন করুন। ডাক্তারদের স্মার্ট প্রেসক্রিপশনে স্যুইচ করতে সাহায্য করুন এবং আপনার আয় বৃদ্ধি করুন।",
            cta1: "পার্টনার হন",
            cta2: "পার্টনার লগইন"
        },
        howItWorks: {
            title: "কীভাবে কাজ করে?",
            subtitle: "৩টি সহজ ধাপে আয় শুরু করুন",
            steps: [
                {
                    number: "১",
                    title: "রেজিস্টার করুন",
                    desc: "মাত্র ১ মিনিটে আপনার পার্টনার অ্যাকাউন্ট তৈরি করুন। আপনি একটি অনন্য ড্যাশবোর্ড এবং রেফারেল লিঙ্ক পাবেন।"
                },
                {
                    number: "२",
                    title: "ডাক্তারদের রেফার করুন",
                    desc: "আপনার লিঙ্ক ডাক্তার বা ক্লিনিকগুলির সাথে শেয়ার করুন। যখন তারা আপনার লিঙ্ক ব্যবহার করে রেজিস্টার করে, তারা চিরকালের জন্য আপনার সাথে যুক্ত থাকে।"
                },
                {
                    number: "३",
                    title: "অর্থ উপার্জন করুন",
                    desc: "যখন তারা পেইড প্ল্যানে আপগ্রেড করে তখন ২০% কমিশন অর্জন করুন। bKash/Nagad এর মাধ্যমে আয় তুলে নিন।"
                }
            ]
        },
        benefits: {
            title: "আমাদের সাথে পার্টনার হন কেন?",
            subtitle: "আমরা সেরা টুলস এবং স্বচ্ছতা প্রদান করি যাতে আপনি মার্কেটিংয়ে ফোকাস করতে পারেন।",
            items: [
                {
                    icon: "dollar",
                    title: "উচ্চ কমিশন হার",
                    desc: "প্রতিটি সফল সাবস্ক্রিপশন পেমেন্টে সমতল ২০% কমিশন। যদি একজন ডাক্তার ৳৬,০০০ পেমেন্ট করে, আপনি ৳১,२००  পাবেন।"
                },
                {
                    icon: "trending",
                    title: "রিয়েল-টাইম ট্র্যাকিং",
                    desc: "আপনার নিবেদিত পার্টনার ড্যাশবোর্ডে ক্লিক, রেজিস্ট্রেশন এবং আয় তাৎক্ষণিকভাবে দেখুন।"
                },
                {
                    icon: "shield",
                    title: "সহজ উইথড্রয়াল",
                    desc: "আপনার ব্যালেন্স ৳৫०० এ পৌঁছানোর পরে যেকোনো সময় উইথড্রয়াল অনুরোধ করুন।"
                }
            ]
        },
        commission: {
            title: "কমিশন উদাহরণ",
            monthly: "ডাক্তার সাবস্ক্রাইব করে (মাসিক)",
            monthlyPrice: "৳ ६००",
            monthlyCommission: "আপনার কমিশন (२०%)",
            monthlyEarnings: "+ ৳ १२०",
            yearly: "ডাক্তার সাবস্ক্রাইব করে (বার্ষিক)",
            yearlyPrice: "৳ ६,०००",
            yearlyCommission: "আপনার কমিশন (२०%)",
            yearlyEarnings: "+ ৳ १,२००"
        },
        policies: {
            title: "প্রোগ্রাম নীতি",
            subtitle: "সিস্টেমকে সবার জন্য ন্যায্য রাখার জন্য গুরুত্বপূর্ণ নিয়ম।",
            policies: [
                {
                    icon: "clock",
                    title: "৭-দিনের হোল্ড পিরিয়ড",
                    desc: "কমিশনগুলি প্রতারণা বা রিফান্ড থেকে সুরক্ষা দিতে পেমেন্টের পরে ৭ দিনের জন্য 'পেন্ডিং' চিহ্নিত করা হয়।"
                },
                {
                    icon: "shield",
                    title: "কোনো সেলফ-রেফারেল নয়",
                    desc: "আপনি নিজের জন্য ডাক্তার অ্যাকাউন্ট তৈরি করতে আপনার নিজের রেফারেল লিঙ্ক ব্যবহার করতে পারবেন না।"
                },
                {
                    icon: "check",
                    title: "পেমেন্ট যাচাইকরণ",
                    desc: "কমিশন শুধুমাত্র তৈরি হয় যখন একজন ডাক্তার bKash এর মাধ্যমে সফল পেমেন্ট করে।"
                },
                {
                    icon: "dollar",
                    title: "উইথড্রয়াল সীমা",
                    desc: "ন্যূনতম উইথড্রয়াল পরিমাণ ৳৫००। আপনি যেকোনো সময় অনুরোধ করতে পারেন এবং २४-४८ ঘন্টার মধ্যে প্রক্রিয়া করা হবে।"
                }
            ]
        },
        faq: {
            title: "শীর্ষ আয়কারী",
            subtitle: "দেখুন আমাদের সফল পার্টনাররা কতটা আয় করছে",
            earnings: [
                { name: "মোঃ করিম", referrals: "৫০+ ডাক্তার", monthly: "৳ १५,०००+", image: "👨‍💼" },
                { name: "ফাতেমা খান", referrals: "३५+ ডাক্তার", monthly: "৳ १०,५००+", image: "👩‍💼" },
                { name: "আহমেদ হাসান", referrals: "२८+ ডাক্তার", monthly: "৳ ८,४००+", image: "👨‍💼" }
            ]
        },
        cta: {
            title: "আয় শুরু করতে প্রস্তুত?",
            subtitle: "কোনো ক্রেডিট কার্ড প্রয়োজন নেই। বিনামূল্যে রেজিস্ট্রেশন।",
            button: "এখনই পার্টনার প্রোগ্রামে যোগ দিন"
        }
    }
};

const PartnerProgram = () => {
    const [language, setLanguage] = useState('bn');
    const t = translations[language];

    return (
        <div className="min-h-screen bg-gray-50 font-sans">
            
            {/* --- NAVIGATION --- */}
            <nav className="sticky top-0 w-full bg-white/95 backdrop-blur-md z-50 border-b border-gray-100">
                <div className="max-w-7xl mx-auto px-4 h-20 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-2">
                        <div className="w-10 h-10 bg-linear-to-br from-indigo-600 to-blue-600 rounded-xl flex items-center justify-center shadow-lg">
                            <HeartPulse className="text-white w-6 h-6" />
                        </div>
                        <span className="font-black text-2xl tracking-tighter bg-clip-text text-transparent bg-linear-to-r from-indigo-900 to-indigo-600">DrChamber</span>
                    </Link>
                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => setLanguage(language === 'bn' ? 'en' : 'bn')}
                            className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition text-sm font-bold text-gray-700"
                        >
                            <Languages size={18} />
                            {language === 'bn' ? 'EN' : 'বাংলা'}
                        </button>
                        <Link to="/login" className="text-sm font-bold text-gray-700 px-4 py-2 hover:bg-gray-100 rounded-lg transition">{t.nav.login}</Link>
                        <Link to="/affiliate/register" className="px-6 py-3 bg-indigo-600 text-white text-sm font-bold rounded-xl hover:bg-indigo-700 transition shadow-xl shadow-indigo-200">
                            {t.nav.signup}
                        </Link>
                    </div>
                </div>
            </nav>
            
            {/* --- HERO SECTION --- */}
            <div className="bg-white border-b border-gray-100 pt-8">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 text-center">
                    <span className="inline-block py-2 px-4 rounded-full bg-indigo-50 text-indigo-600 text-sm font-black mb-6 border border-indigo-200">
                        {t.hero.badge}
                    </span>
                    <h1 className="text-5xl md:text-7xl font-black text-gray-900 mb-6 tracking-tight leading-tight">
                        {language === 'bn' ? (
                            <>বাংলাদেশে স্বাস্থ্যসেবা <br/><span className="text-transparent bg-clip-text bg-linear-to-r from-indigo-600 to-blue-500">ডিজিটালাইজ করে আয় করুন</span></>
                        ) : (
                            <>Earn Money by Digitalizing <br/><span className="text-transparent bg-clip-text bg-linear-to-r from-indigo-600 to-blue-500">Healthcare in Bangladesh</span></>
                        )}
                    </h1>
                    <p className="text-xl text-gray-600 max-w-2xl mx-auto mb-10 leading-relaxed font-bold">
                        {t.hero.subtitle}
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <Link to="/affiliate/register" className="px-8 py-4 bg-indigo-600 text-white font-bold rounded-xl hover:bg-indigo-700 shadow-lg hover:shadow-xl transition transform hover:-translate-y-1 flex items-center justify-center gap-2 text-lg">
                            <Target size={24} />
                            {t.hero.cta1} <ArrowRight size={20} />
                        </Link>
                        <Link to="/affiliate/login" className="px-8 py-4 bg-white border-2 border-indigo-600 text-indigo-600 font-bold rounded-xl hover:bg-indigo-50 transition text-lg">
                            {t.hero.cta2}
                        </Link>
                    </div>

                    {/* Key Stats */}
                    <div className="mt-20 grid grid-cols-1 md:grid-cols-3 gap-8 max-w-3xl mx-auto">
                        <div className="p-6 bg-linear-to-br from-indigo-50 to-blue-50 rounded-2xl">
                            <p className="text-4xl font-black text-indigo-600 mb-2">৳ १,२००</p>
                            <p className="text-gray-700 font-bold text-sm">{language === 'bn' ? 'প্রতি বার্ষিক রেফার থেকে আয়' : 'Per yearly referral earned'}</p>
                        </div>
                        <div className="p-6 bg-linear-to-br from-green-50 to-emerald-50 rounded-2xl">
                            <p className="text-4xl font-black text-green-600 mb-2">५००+</p>
                            <p className="text-gray-700 font-bold text-sm">{language === 'bn' ? 'সক্রিয় পার্টনার' : 'Active partners'}</p>
                        </div>
                        <div className="p-6 bg-linear-to-br from-purple-50 to-pink-50 rounded-2xl">
                            <p className="text-4xl font-black text-purple-600 mb-2">२०%</p>
                            <p className="text-gray-700 font-bold text-sm">{language === 'bn' ? 'চিরস্থায়ী কমিশন' : 'Lifetime commission'}</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* --- HOW IT WORKS --- */}
            <section className="py-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="text-center mb-20">
                    <h2 className="text-5xl font-black text-gray-900 mb-4">{t.howItWorks.title}</h2>
                    <p className="text-xl text-gray-600 font-bold">{t.howItWorks.subtitle}</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative">
                    {/* Connecting Line (Desktop Only) */}
                    <div className="hidden md:block absolute top-20 left-0 w-full h-1 bg-linear-to-r from-indigo-600 via-indigo-400 to-transparent -z-10"></div>

                    {t.howItWorks.steps.map((step, idx) => (
                        <div key={idx} className="bg-white p-8 rounded-3xl shadow-lg border-2 border-indigo-200 text-center relative hover:shadow-2xl transition transform hover:-translate-y-2">
                            <div className="w-20 h-20 bg-linear-to-br from-indigo-600 to-blue-600 text-white text-4xl font-black rounded-full flex items-center justify-center mx-auto mb-6 border-4 border-white shadow-xl">
                                {step.number}
                            </div>
                            <h3 className="text-2xl font-black text-gray-900 mb-4">{step.title}</h3>
                            <p className="text-gray-600 font-bold text-base leading-relaxed">{step.desc}</p>
                            <div className="mt-6 pt-6 border-t border-gray-100">
                                <span className="inline-block px-3 py-1 bg-indigo-50 text-indigo-600 text-xs font-black rounded-full">{language === 'bn' ? 'ধাপ' : 'STEP'} {step.number}</span>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* --- COMMISSION STRUCTURE & FACILITIES --- */}
            <section className="py-24 bg-linear-to-b from-gray-50 to-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                        <div>
                            <h2 className="text-5xl font-black text-gray-900 mb-6">{t.benefits.title}</h2>
                            <p className="text-lg text-gray-600 mb-12 leading-relaxed font-bold">
                                {t.benefits.subtitle}
                            </p>
                            
                            <div className="space-y-6">
                                {t.benefits.items.map((item, idx) => {
                                    const iconMap = {
                                        'dollar': <DollarSign size={28} />,
                                        'trending': <TrendingUp size={28} />,
                                        'shield': <Shield size={28} />
                                    };
                                    return (
                                        <div key={idx} className="flex gap-4 p-4 bg-white rounded-2xl border-2 border-indigo-100 hover:border-indigo-400 transition">
                                            <div className="bg-linear-to-br from-indigo-50 to-blue-50 p-4 rounded-xl h-fit text-indigo-600 shrink-0">
                                                {iconMap[item.icon]}
                                            </div>
                                            <div className="grow">
                                                <h4 className="text-xl font-black text-gray-900 mb-2">{item.title}</h4>
                                                <p className="text-gray-600 font-bold text-sm leading-relaxed">{item.desc}</p>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Visual Card */}
                        <div className="bg-linear-to-br from-indigo-900 to-indigo-800 rounded-3xl p-10 border-2 border-indigo-700 shadow-2xl relative overflow-hidden">
                            <div className="absolute top-0 right-0 p-4 opacity-10"><Zap size={150} /></div>
                            <h3 className="text-3xl font-black text-white mb-8 relative z-10">{t.commission.title}</h3>
                            
                            <div className="space-y-4 relative z-10">
                                <div className="flex justify-between items-center p-4 bg-white/10 backdrop-blur rounded-2xl border border-white/20">
                                    <span className="text-white font-bold">{t.commission.monthly}</span>
                                    <span className="font-black text-gray-200 text-lg">{t.commission.monthlyPrice}</span>
                                </div>
                                <div className="flex justify-between items-center p-4 bg-green-500/20 border border-green-400/50 rounded-2xl">
                                    <span className="text-green-300 font-black">{t.commission.monthlyCommission}</span>
                                    <span className="font-black text-green-300 text-lg">{t.commission.monthlyEarnings}</span>
                                </div>
                            </div>
                            
                            <div className="mt-8 space-y-4 relative z-10">
                                <div className="flex justify-between items-center p-4 bg-white/10 backdrop-blur rounded-2xl border border-white/20">
                                    <span className="text-white font-bold">{t.commission.yearly}</span>
                                    <span className="font-black text-gray-200 text-lg">{t.commission.yearlyPrice}</span>
                                </div>
                                <div className="flex justify-between items-center p-4 bg-yellow-500/20 border border-yellow-400/50 rounded-2xl">
                                    <span className="text-yellow-300 font-black">{t.commission.yearlyCommission}</span>
                                    <span className="font-black text-yellow-300 text-lg">{t.commission.yearlyEarnings}</span>
                                </div>
                            </div>

                            <div className="mt-10 pt-8 border-t border-white/20 relative z-10">
                                <p className="text-indigo-200 text-sm font-bold mb-4">{language === 'bn' ? '💰 মাসিক সম্ভাব্য আয়' : '💰 Possible Monthly Earnings'}</p>
                                <div className="bg-white/10 p-4 rounded-2xl border border-white/20">
                                    <p className="text-white font-black text-2xl">
                                        {language === 'bn' ? '০ থেকে ৳ २,५००+' : '০ to ৳ २,५००+'}
                                    </p>
                                    <p className="text-indigo-200 text-xs font-bold mt-2">{language === 'bn' ? '১০-२०+ রেফার বেসড' : 'Based on 10-२०+ referrals'}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* --- POLICIES --- */}
            <section className="py-24 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="text-center mb-16">
                    <h2 className="text-5xl font-black text-gray-900 mb-4">{t.policies.title}</h2>
                    <p className="text-lg text-gray-600 font-bold">{t.policies.subtitle}</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {t.policies.policies.map((policy, idx) => {
                        const iconMap = {
                            'clock': <Clock className="text-indigo-500" size={24} />,
                            'shield': <Shield className="text-red-500" size={24} />,
                            'check': <CheckCircle className="text-green-500" size={24} />,
                            'dollar': <DollarSign className="text-blue-500" size={24} />
                        };
                        const colorBorders = ['border-indigo-500', 'border-red-500', 'border-green-500', 'border-blue-500'];
                        
                        return (
                            <div key={idx} className={`bg-white p-6 rounded-2xl shadow-lg border-l-4 ${colorBorders[idx]} hover:shadow-xl transition transform hover:-translate-y-1`}>
                                <h4 className="flex items-center gap-3 font-black text-lg mb-4">
                                    {iconMap[policy.icon]}
                                    {policy.title}
                                </h4>
                                <p className="text-gray-700 font-bold text-sm leading-relaxed">{policy.desc}</p>
                            </div>
                        );
                    })}
                </div>
            </section>

            {/* --- TOP EARNERS SECTION --- */}
            {/* <section className="py-24 bg-linear-to-b from-gray-50 to-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <h2 className="text-5xl font-black text-gray-900 mb-4">{t.faq.title}</h2>
                    <p className="text-lg text-gray-600 font-bold mb-16">{t.faq.subtitle}</p>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {t.faq.earnings.map((earner, idx) => (
                            <div key={idx} className="bg-white rounded-3xl p-8 shadow-lg border-2 border-indigo-100 hover:shadow-2xl transition transform hover:-translate-y-2 relative overflow-hidden">
                                <div className="absolute top-0 right-0 text-6xl opacity-10">{earner.image}</div>
                                <div className="relative z-10">
                                    <div className="text-6xl mb-4">{earner.image}</div>
                                    <h4 className="text-2xl font-black text-gray-900 mb-2">{earner.name}</h4>
                                    <div className="mb-6 pt-4 border-t border-gray-200">
                                        <p className="text-sm font-bold text-gray-600 mb-2">{earner.referrals}</p>
                                        <p className="text-3xl font-black text-green-600">{earner.monthly}</p>
                                    </div>
                                    <span className="inline-block px-4 py-2 bg-green-100 text-green-700 rounded-full text-xs font-black">{language === 'bn' ? 'সক্রিয় উপার্জনকারী' : 'Active Earner'}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section> */}

            {/* --- CTA FOOTER --- */}
            <div className="bg-linear-to-r from-indigo-600 to-blue-600 text-white py-24 relative overflow-hidden">
                <div className="absolute inset-0 opacity-10">
                    <div className="absolute top-0 right-0 w-80 h-80 bg-white rounded-full -mr-40 -mt-40"></div>
                    <div className="absolute bottom-0 left-0 w-60 h-60 bg-white rounded-full -ml-30 -mb-30"></div>
                </div>
                <div className="max-w-4xl mx-auto px-4 text-center relative z-10">
                    <h2 className="text-5xl font-black mb-6">{t.cta.title}</h2>
                    <p className="text-xl text-blue-100 mb-12 font-bold">{t.cta.subtitle}</p>
                    <Link to="/affiliate/register" className="inline-flex items-center gap-4 px-12 py-6 bg-white text-indigo-600 font-black text-lg rounded-2xl shadow-2xl hover:shadow-3xl transition transform hover:scale-105">
                        {t.cta.button} <ArrowRight size={28} />
                    </Link>
                </div>
            </div>
        </div>
    );
};

export default PartnerProgram;